export const FINANCIAL_INSTITUTION_SLUG = 'DecFinInstName';
export const EMPTY_INSTITUTIONS = [];

export const findQuestionBySlug = (questions = [], slug) => {
  for (const question of questions) {
    if (question.slug === slug) {
      return question;
    }

    const childQuestion = findQuestionBySlug(question.children || [], slug);

    if (childQuestion) {
      return childQuestion;
    }
  }

  return null;
};

export const findQuestionPathBySlug = (questions = [], slug, path = []) => {
  for (const question of questions) {
    const nextPath = [...path, question];

    if (question.slug === slug) {
      return nextPath;
    }

    const childPath = findQuestionPathBySlug(
      question.children || [],
      slug,
      nextPath
    );

    if (childPath) {
      return childPath;
    }
  }

  return null;
};

export const getQuestionsFromList = (questionList = []) =>
  questionList.flatMap(({ questions = [] }) => questions);

export const getQuestionsFromQuestionData = (questionData) =>
  questionData?.list
    ? getQuestionsFromList(questionData.list)
    : questionData?.questions || [];

export const getQuestionAnswerPayload = ({ question, answer, value }) => {
  const shouldCreateAnswer = ['single', 'multiple'].includes(
    question?.answerType
  );

  return {
    questionId: question.id,
    answerId: shouldCreateAnswer ? null : answer?.id || null,
    value,
    action: !shouldCreateAnswer && answer?.id ? 'update' : 'create',
  };
};

export const createEmptyInstitution = (clientId) => ({
  answerId: null,
  clientId,
  name: '',
  originalName: '',
  error: '',
  pendingArchive: false,
  restoredFromArchive: false,
});

export const isArchivedInstitution = (institution) =>
  institution?.status === 'suspended';

export const getInstitutionName = (institution) => {
  if (institution?.name) {
    return institution.name;
  }

  if (institution?.value && typeof institution.value === 'object') {
    return institution.value.name || '';
  }

  return institution?.value || '';
};

export const mapInstitutionToRow = (
  institution,
  clientId,
  restoredFromArchive = false
) => ({
  answerId: institution.id,
  clientId,
  name: getInstitutionName(institution),
  originalName: getInstitutionName(institution),
  error: '',
  pendingArchive: false,
  restoredFromArchive,
});

export const getInstitutionDeleteTitle = (t, institutionName) =>
  institutionName
    ? t('dataEntry.archiveDeleteTitleWithValues', {
        item: t('Financial institution'),
        values: institutionName,
      })
    : t('dataEntry.archiveDeleteTitle', {
        item: t('Financial institution'),
      });

export const hasInstitutionValue = (institution) =>
  !!institution?.name?.trim?.();

export const getInstitutionDeleteType = (institution) =>
  institution.answerId ? 'archive' : 'delete';

export const shouldSkipInstitutionDeleteConfirmation = (institution) =>
  getInstitutionDeleteType(institution) === 'delete' &&
  !hasInstitutionValue(institution);

export const getInstitutionDeleteDescription = (t, institution) =>
  getInstitutionDeleteType(institution) === 'archive'
    ? t('dataEntry.archiveDeleteDescription')
    : t('dataEntry.permanentDeleteDescription');

export const getInstitutionDeleteConfirmTitle = (t, institution) =>
  getInstitutionDeleteType(institution) === 'archive'
    ? getInstitutionDeleteTitle(t, institution.name)
    : t('dataEntry.permanentDeleteTitle', {
        item: t('Financial institution').toLowerCase(),
      });

export const getFinancialInstitutionAction = (institution) => {
  if (institution.restoredFromArchive) {
    return institution.name === institution.originalName ? 'restore' : 'update';
  }

  return institution.answerId ? 'update' : 'create';
};

export const buildFinancialInstitutionAnswers = ({
  institutions,
  deletedInstitutionIds,
  questionId,
  parentQuestion,
  parentAnswer,
  parentQuestionValue = null,
}) => {
  const answers = [
    ...institutions.map((institution) => ({
      questionId,
      answerId: institution.answerId,
      value: { name: institution.name },
      action: getFinancialInstitutionAction(institution),
    })),
    ...deletedInstitutionIds.map((answerId) => ({
      questionId,
      answerId,
      value: '',
      action: 'archive',
    })),
  ];

  if (parentQuestionValue && parentQuestion?.id) {
    answers.push(
      getQuestionAnswerPayload({
        question: parentQuestion,
        answer: parentAnswer,
        value: parentQuestionValue,
      })
    );
  }

  return answers;
};
